import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { chunk } from 'lodash';
import { COLORS } from 'Components/utils/color/colors';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { themes } from './LabeledIconsList.themes';
export type LabeledIcon = {
  icon: IconName;
  labels: Array<string>;
  buttonText?: string;
};
type Props = {
  color?: Colors;
  items: Array<LabeledIcon>;
  theme?: keyof typeof themes;
};

const LabeledIconsListItems = ({
  color = COLORS.BLACK,
  isMoreThanOneList,
  items = [],
  listIndex = 0,
  theme = 'default',
}: Props & {
  listIndex: number;
  isMoreThanOneList?: boolean;
}) => {
  const {
    thIcon,
    thIconWidth,
    thItem,
    thItemNth,
    thLabels,
    thListRoot,
    thListWidth,
    thListFirst,
    thListSubsequent,
  } = themes[theme];
  return (
    <ul
      className={classnames(`text-${color}`, thListRoot, {
        [thListWidth]: isMoreThanOneList,
        [thListFirst]: listIndex === 0,
        [thListSubsequent]: listIndex !== 0,
      })}
    >
      {items.map(({ icon, labels }, idx) => (
        <li
          className={classnames(thItem, {
            [thItemNth]: idx > 0,
          })}
          data-testid="LabeledIconsList-item"
          key={idx}
        >
          <div className={thIcon}>
            <Icon name={icon} width={thIconWidth} alt={(labels || []).join('/')} />
          </div>
          {!!labels.length && (
            <div className={thLabels}>
              {labels.map((label) => (
                <div data-testid="LabeledIconsList-label" key={label}>
                  {label}
                </div>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

export const LabeledIconsList = (props: Props) => {
  const { theme = 'default', items = [] } = props;

  if (!items.length) {
    return null;
  }

  const shouldSplitItems = theme !== 'default' && items.length > 2;
  const { thRoot } = themes[theme];
  const splitItems = chunk([...items], Math.ceil(items.length / 2));
  const isMoreThanOneList = splitItems.length > 1;
  return (
    <div className={thRoot}>
      {shouldSplitItems ? (
        splitItems.map((splitItem, idx) => (
          <LabeledIconsListItems
            {...props}
            key={idx}
            listIndex={idx}
            isMoreThanOneList={isMoreThanOneList}
            items={splitItem}
          />
        ))
      ) : (
        <LabeledIconsListItems {...props} listIndex={0} items={items} />
      )}
    </div>
  );
};
